import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Mail, MailCheck, MailX, AlertTriangle, RefreshCcw, Send, CheckCircle2, XCircle,
} from "lucide-react";
import { adminEmailHealth, adminEmailRecent, adminEmailTest } from "../../lib/api";

/**
 * Admin → Email Health.
 *
 * Three panels:
 *   1. Health summary tile — 24h failure rate + alert badge that flips
 *      to "warning" at 10% and "critical" at 25%. SMTP / Resend / Both
 *      backend breakdown. Last success + last failure timestamps.
 *   2. Send Test Email — fires through the SAME `send_email` pipeline
 *      as customer mail, so a green checkmark proves end-to-end works.
 *   3. Recent log — last 50 send attempts (newest first) with full
 *      error text on failure rows. Manual refresh button — no auto-poll
 *      to keep the request count low.
 *
 * Catches outages early: instead of finding out a customer never got
 * their receipt, the operator sees the failure within seconds.
 */
function StatTile({ label, value, hint, tone = "neutral" }) {
  const toneClass = {
    neutral: "border-[#222] bg-[#0a0a0a] text-zinc-100",
    good: "border-emerald-700 bg-emerald-950/40 text-emerald-100",
    warn: "border-amber-700 bg-amber-950/40 text-amber-100",
    bad: "border-red-800 bg-red-950/40 text-red-100",
  }[tone];
  return (
    <div className={`border ${toneClass} p-4`} data-testid={`stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="font-mono text-[10px] tracking-widest text-zinc-400 mb-2">{label}</div>
      <div className="font-mono font-bold text-2xl">{value}</div>
      {hint && <div className="font-body text-xs text-zinc-500 mt-1">{hint}</div>}
    </div>
  );
}

function AlertBadge({ level }) {
  if (level === "ok") {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-emerald-700 bg-emerald-950/40 text-emerald-300 font-mono text-[10px] tracking-widest" data-testid="alert-badge-ok">
        <CheckCircle2 size={12} /> HEALTHY
      </span>
    );
  }
  if (level === "warning") {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-amber-700 bg-amber-950/40 text-amber-300 font-mono text-[10px] tracking-widest" data-testid="alert-badge-warning">
        <AlertTriangle size={12} /> WARNING — 10%+ FAILURE
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-red-700 bg-red-950/40 text-red-300 font-mono text-[10px] tracking-widest" data-testid="alert-badge-critical">
      <XCircle size={12} /> CRITICAL — 25%+ FAILURE
    </span>
  );
}

export default function EmailHealthTab({ pw }) {
  const [health, setHealth] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [h, r] = await Promise.all([
        adminEmailHealth(pw, 24),
        adminEmailRecent(pw, 50),
      ]);
      setHealth(h);
      setRecent(r.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load email health");
    } finally {
      setLoading(false);
    }
  }, [pw]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleSendTest = async () => {
    const to = (testTo || "").trim();
    if (!to || !to.includes("@")) {
      toast.error("Enter a valid email address");
      return;
    }
    setTesting(true);
    try {
      const res = await adminEmailTest(pw, to);
      if (res.sent) {
        toast.success(`Test email sent to ${to}`);
      } else {
        toast.error(`Test email FAILED — see the Recent Log row for error details`);
      }
      // Refresh so the new attempt shows in the log
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="email-health-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl uppercase tracking-tight">Email Health</h2>
          <p className="font-body text-sm text-zinc-400 mt-1">
            24-hour rolling deliverability. Catch SMTP outages before a customer complains.
          </p>
        </div>
        <button
          onClick={refresh}
          disabled={loading}
          data-testid="email-health-refresh"
          className="inline-flex items-center gap-2 px-4 py-2 border border-[#222] bg-[#0a0a0a] hover:border-[var(--tc-red)] font-mono text-xs tracking-widest text-zinc-300 disabled:opacity-50"
        >
          <RefreshCcw size={14} className={loading ? "animate-spin" : ""} />
          {loading ? "REFRESHING…" : "REFRESH"}
        </button>
      </div>

      {/* Summary */}
      {health && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <AlertBadge level={health.alert_level} />
            <span className="font-mono text-[10px] tracking-widest text-zinc-500">
              WINDOW: LAST {health.window_hours}H
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatTile
              label="Total Sent"
              value={health.total}
              hint={`${Object.entries(health.backends || {}).map(([k, v]) => `${v} via ${k}`).join(", ") || "no backends"}`}
              tone="neutral"
            />
            <StatTile
              label="Delivered"
              value={health.sent}
              tone={health.failed === 0 ? "good" : "neutral"}
            />
            <StatTile
              label="Failed"
              value={health.failed}
              tone={health.failed === 0 ? "good" : "bad"}
            />
            <StatTile
              label="Failure Rate"
              value={`${health.failure_rate_pct}%`}
              tone={health.alert_level === "ok" ? "good" : health.alert_level === "warning" ? "warn" : "bad"}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="border border-[#222] bg-[#0a0a0a] p-4">
              <div className="font-mono text-[10px] tracking-widest text-zinc-400 mb-2 flex items-center gap-2">
                <MailCheck size={12} className="text-emerald-400" /> LAST SUCCESS
              </div>
              {health.last_success ? (
                <>
                  <div className="font-mono text-sm text-zinc-100">{health.last_success.to}</div>
                  <div className="font-body text-xs text-zinc-500 mt-1">{new Date(health.last_success.sent_at).toLocaleString()}</div>
                </>
              ) : (
                <div className="font-body text-sm text-zinc-500">No successful sends in window</div>
              )}
            </div>
            <div className="border border-[#222] bg-[#0a0a0a] p-4">
              <div className="font-mono text-[10px] tracking-widest text-zinc-400 mb-2 flex items-center gap-2">
                <MailX size={12} className="text-red-400" /> LAST FAILURE
              </div>
              {health.last_failure ? (
                <>
                  <div className="font-mono text-sm text-zinc-100">{health.last_failure.to}</div>
                  <div className="font-body text-xs text-zinc-500 mt-1">{new Date(health.last_failure.sent_at).toLocaleString()}</div>
                  <div className="font-mono text-[10px] text-red-300 mt-2 truncate" title={health.last_failure.error}>{health.last_failure.error}</div>
                </>
              ) : (
                <div className="font-body text-sm text-zinc-500">No failures in window 🎉</div>
              )}
            </div>
          </div>
          <div className="border border-[#222] bg-[#0a0a0a] p-3 flex flex-wrap gap-x-6 gap-y-1 font-mono text-[10px] tracking-widest text-zinc-500">
            <span>SMTP_USER: {health.smtp_user_configured ? "✓ SET" : "✗ MISSING"}</span>
            <span>SMTP_PASSWORD: {health.smtp_password_set ? "✓ SET" : "✗ MISSING"}</span>
          </div>
        </div>
      )}

      {/* Send Test Email */}
      <div className="border border-[#222] bg-[#0a0a0a] p-5">
        <h3 className="font-display text-lg uppercase tracking-tight flex items-center gap-2">
          <Send size={16} className="text-[var(--tc-red)]" /> Send Test Email
        </h3>
        <p className="font-body text-sm text-zinc-400 mt-1 mb-3">
          Runs through the same pipeline as customer mail. Result lands in the Recent Log below.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="email"
            placeholder="recipient@example.com"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            data-testid="email-test-to"
            className="flex-1 bg-black border border-[#333] px-3 py-2 font-mono text-sm text-zinc-100 placeholder-zinc-600 focus:border-[var(--tc-red)] focus:outline-none"
          />
          <button
            onClick={handleSendTest}
            disabled={testing}
            data-testid="email-test-send-btn"
            className="px-5 py-2 bg-[var(--tc-red)] text-white font-mono text-xs tracking-widest hover:bg-red-700 disabled:opacity-50"
          >
            {testing ? "SENDING…" : "SEND TEST"}
          </button>
        </div>
      </div>

      {/* Recent log */}
      <div>
        <h3 className="font-display text-lg uppercase tracking-tight mb-3 flex items-center gap-2">
          <Mail size={16} /> Recent Email Log
          <span className="font-mono text-[10px] tracking-widest text-zinc-500">— LAST {recent.length}</span>
        </h3>
        <div className="border border-[#222] bg-[#0a0a0a] overflow-x-auto">
          <table className="w-full font-mono text-xs" data-testid="email-log-table">
            <thead className="bg-[#0f0f0f] border-b border-[#222]">
              <tr>
                <th className="text-left px-3 py-2 tracking-widest text-zinc-500 text-[10px]">STATUS</th>
                <th className="text-left px-3 py-2 tracking-widest text-zinc-500 text-[10px]">TIME</th>
                <th className="text-left px-3 py-2 tracking-widest text-zinc-500 text-[10px]">TO</th>
                <th className="text-left px-3 py-2 tracking-widest text-zinc-500 text-[10px]">SUBJECT</th>
                <th className="text-left px-3 py-2 tracking-widest text-zinc-500 text-[10px]">BACKEND</th>
                <th className="text-left px-3 py-2 tracking-widest text-zinc-500 text-[10px]">ERROR</th>
              </tr>
            </thead>
            <tbody>
              {recent.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-zinc-500 font-body">No email attempts yet.</td></tr>
              ) : recent.map((row, i) => (
                <tr
                  key={i}
                  data-testid={`email-log-row-${i}`}
                  className={`border-b border-[#1a1a1a] ${row.success ? "" : "bg-red-950/20"}`}
                >
                  <td className="px-3 py-2">
                    {row.success ? (
                      <CheckCircle2 size={14} className="text-emerald-400" aria-label="sent" />
                    ) : (
                      <XCircle size={14} className="text-red-400" aria-label="failed" />
                    )}
                  </td>
                  <td className="px-3 py-2 text-zinc-400 whitespace-nowrap">
                    {new Date(row.sent_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </td>
                  <td className="px-3 py-2 text-zinc-100 max-w-[180px] truncate" title={row.to}>{row.to}</td>
                  <td className="px-3 py-2 text-zinc-300 max-w-[260px] truncate" title={row.subject}>{row.subject}</td>
                  <td className="px-3 py-2 text-zinc-500">{row.backend}</td>
                  <td className="px-3 py-2 text-red-300 max-w-[260px] truncate" title={row.error || ""}>
                    {row.error || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
